import { DataTableSortStatus } from 'mantine-datatable';
import { usePagination } from '@mantine/hooks';
import { BaseRecord, SortableColumn } from '../types';
import { useEffect, useMemo } from 'react';
import usePageNavigation from './usePageNavigation';
import useLocalPageNavigation from './useLocalPageNavigation';
import useSortableData from './useSortableData';
import { PaginationData } from './useTable';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type UseClientPaginationOptions<T> = {
  data: T[];
  columns: SortableColumn<T>[];
  searchTerm: string;
  filterPredicate?: (item: T, search: string) => boolean;
  paginationDependencies?: unknown;
  pageSize?: number;
  useLocalPagination?: boolean;
  enabled?: boolean;
};

type ClientPaginationResult<T> = {
  paginatedData: T[];
  pagination: PaginationData;
  totalRecords: number;
  sortStatus: DataTableSortStatus<T>;
  onSortStatusChange: (status: DataTableSortStatus<T>) => void;
};

export const useClientPagination = <T extends BaseRecord>(
  options: UseClientPaginationOptions<T>,
): ClientPaginationResult<T> => {
  const {
    data,
    columns,
    searchTerm,
    filterPredicate,
    paginationDependencies,
    pageSize = 10,
    useLocalPagination = false,
    enabled = true,
  } = options;

  // Conditionally use local or URL-based pagination
  const urlPageNav = usePageNavigation();
  const localPageNav = useLocalPageNavigation();
  const { currentPage, navigateToPage } = useLocalPagination ? localPageNav : urlPageNav;

  const filteredData = useMemo(() => {
    try {
      if (!filterPredicate) {
        return data;
      }
      return data.filter(item => filterPredicate(item, searchTerm));
    } catch (error) {
      notifyAndLogError(
        'Filtering failed',
        'An error occurred while filtering table data. Please try again.',
        error,
        { searchTerm, filterPredicate: !!filterPredicate },
      );
      return data;
    }
  }, [data, filterPredicate, searchTerm]);

  // When inactive (server mode), navigateToPage(1) must not run — it navigates to the
  // bare path and wipes the query string, including the server hook's ?page param
  useEffect(() => {
    if (!enabled) {
      return;
    }
    navigateToPage(1);
  }, [searchTerm, paginationDependencies, enabled]);

  const { sortStatus, setSortStatus, sortedData } = useSortableData(filteredData, columns);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const pagination = usePagination({
    total: totalPages,
    page: currentPage,
    onChange: navigateToPage,
  });

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.active - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, pagination.active, pageSize]);

  return {
    paginatedData,
    pagination,
    totalRecords: filteredData.length,
    sortStatus,
    onSortStatusChange: setSortStatus,
  };
};
